import { useState, useEffect } from 'react';
import api from '../api';
import { Search, Filter } from 'lucide-react';
import FeedbackForm from '../pages/FeedbackForm';
import Pagination from '../components/Pagination';

function FeedbackPage({ readOnly = false }) {
  const [expenses, setExpenses] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchApprovedExpenses = async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/feedback/approved');
      const data = Array.isArray(res.data) ? res.data : [];
      setExpenses(data);
    } catch (err) {
      console.error('Error fetching approved expenses:', err);
      setError('Failed to load expenses. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApprovedExpenses();
  }, []);

  // Filter and search
  useEffect(() => {
    let data = [...expenses];
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      data = data.filter(e =>
        String(e.expense_id || '').toLowerCase().includes(q) ||
        String(e.expense_name || '').toLowerCase().includes(q) ||
        String(e.vendor_name || '').toLowerCase().includes(q)
      );
    }
    if (statusFilter && statusFilter !== 'All') {
      data = data.filter(e => String(e.status || '').toLowerCase() === statusFilter.toLowerCase());
    }
    setFiltered(data);
    setCurrentPage(1);
  }, [searchTerm, statusFilter, expenses]);

  const handleFeedbackClick = (expense) => {
    if (readOnly) return; // view-only mode
    setSelectedExpense(expense);
    setShowFeedbackForm(true);
  };

  if (loading) return <div className="p-6">Loading expenses...</div>;
  if (error) return <div className="p-6 text-red-600">{error}</div>;

  // Pagination
  const totalPages = Math.ceil(filtered.length / itemsPerPage) || 1;
  const paginated = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const renderStars = (val) => {
    const r = Number(val) || 0;
    return (
      <span className="text-yellow-400">
        {Array.from({ length: 5 }, (_, i) => (
          <span key={i} className={i < r ? '' : 'text-gray-300'}>★</span>
        ))}
      </span>
    );
  };

  return (
    <div className="p-6 w-full space-y-6" style={{ color: 'var(--text)' }}>
      <div>
        <h1 className="text-2xl font-bold mb-1" style={{ color: 'var(--heading-color, var(--text))' }}>Feedback</h1>
        <p className="text-sm" style={{ color: 'var(--muted)' }}>Provide vendor ratings and reviews for approved expenses</p>
      </div>

      <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-4">
        {/* Search */}
        <div className="relative w-full md:w-1/3">
          <Search className="absolute top-2.5 left-3 text-gray-400" />
          <input
            type="text"
            className="pl-10 pr-4 py-2 border rounded-md w-full shadow-sm"
            style={{ background: 'var(--panel)', color: 'var(--text)', borderColor: 'var(--border)' }}
            placeholder="Search by ID, Name, or Vendor..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Status Filter */}
        <div className="flex items-center gap-2">
          <Filter className="text-gray-500" />
          <select
            className="border px-3 py-2 rounded-md shadow-sm"
            style={{ background: 'var(--panel)', color: 'var(--text)', borderColor: 'var(--border)' }}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option>All</option>
            <option>Draft</option>
            <option>Approved</option>
            <option>Pending Level 1</option>
            <option>Pending Level 2</option>
            <option>Pending Level 3</option>
            <option>Payment Pending</option>
            <option>Payment Done</option>
            <option>Rejected</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl shadow-sm border p-4" style={{ background: 'var(--panel)', borderColor: 'var(--border)' }}>
        <p className="text-sm mb-2" style={{ color: 'var(--muted)' }}>{filtered.length} expenses found</p>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm text-left border-collapse" style={{ color: 'var(--text)' }}>
            <thead className="border-b" style={{ borderColor: 'var(--border)', color: 'var(--muted)' }}>
              <tr>
                <th className="p-2">Expense ID</th>
                <th className="p-2">Expense Category</th>
                <th className="p-2">Vendor Name</th>
                <th className="p-2">Date</th>
                <th className="p-2">Status</th>
                <th className="p-2">Rating</th>
                <th className="p-2">Remarks</th>
                {!readOnly && <th className="p-2">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {paginated.map((e) => (
                <tr key={e.expense_id} className="border-b" style={{ borderColor: 'var(--border)' }}>
                  <td className="p-3 font-normal" style={{ color: 'var(--text)' }}>{e.expense_id}</td>
                  <td className="p-3">{e.expense_category}</td>
                  <td className="p-3">{e.vendor_name}</td>
                  <td className="p-3">
                    {(() => {
                      const raw = e.ed_date || e.date || e.created_at;
                      const d = raw ? new Date(raw) : null;
                      return d && !isNaN(d) ? d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '-';
                    })()}
                  </td>
                  <td className="p-3">
                    <span
                      className={`px-2 py-1 text-xs rounded-full font-medium`}
                      style={{
                        background:
                          e.status === 'Approved' ? 'rgba(34,197,94,0.15)'
                          : e.status === 'Rejected' ? 'rgba(239,68,68,0.15)'
                          : String(e.status || '').startsWith('Pending') ? 'rgba(234,179,8,0.18)'
                          : e.status === 'Payment Pending' ? 'rgba(245,158,11,0.18)'
                          : e.status === 'Payment Done' ? 'rgba(59,130,246,0.18)'
                          : 'rgba(148,163,184,0.16)',
                        color:
                          e.status === 'Approved' ? '#22c55e'
                          : e.status === 'Rejected' ? '#ef4444'
                          : String(e.status || '').startsWith('Pending') ? '#a16207'
                          : e.status === 'Payment Pending' ? '#a16207'
                          : e.status === 'Payment Done' ? '#1d4ed8'
                          : 'var(--muted)'
                      }}
                    >
                      {e.status}
                    </span>
                  </td>
                  <td className="p-3">
                    {e.has_feedback ? renderStars(e.feedback_rating) : <span style={{ color: 'var(--muted)' }}>—</span>}
                  </td>
                  <td className="p-3 max-w-xs">
                    {e.has_feedback && e.feedback_comments
                      ? <span title={e.feedback_comments}>{String(e.feedback_comments).length > 40 ? `${String(e.feedback_comments).slice(0,40)}…` : e.feedback_comments}</span>
                      : <span style={{ color: 'var(--muted)' }}>—</span>}
                  </td>
                  {!readOnly && (
                    <td className="p-3">
                      <button
                        onClick={() => handleFeedbackClick(e)}
                        className=""
                        style={{ color: 'var(--accent)' }}
                      >
                        {e.has_feedback ? 'Update' : 'Rating'}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
              {paginated.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center py-4" style={{ color: 'var(--muted)' }}>No expenses found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {filtered.length > 0 && (
          <div className="border-t mt-2" style={{ borderColor: 'var(--border)' }}>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(p) => setCurrentPage(p)}
              totalItems={filtered.length}
              itemsPerPage={itemsPerPage}
            />
          </div>
        )}
      </div>

      {!readOnly && showFeedbackForm && selectedExpense && (
        <FeedbackForm
          expenseId={selectedExpense.expense_id}
          initialRating={selectedExpense.has_feedback ? selectedExpense.feedback_rating : 0}
          initialRemarks={selectedExpense.has_feedback ? (selectedExpense.feedback_comments || '') : ''}
          isUpdate={!!selectedExpense.has_feedback}
          onClose={(success) => {
            setShowFeedbackForm(false);
            if (success) {
              fetchApprovedExpenses();
            }
          }}
        />
      )}
    </div>
  );
}

export default FeedbackPage;